import { Icon } from "@sellar-io/components";
import { Link } from "react-router";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useSellarUser } from "../../../hooks/useSellarUser.shared";
import { useLegalEntityCompanyCreditInfo } from "../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import {
  SELLAR_CREDIT_TAB,
  SELLAR_CREDIT_MANAGE_COMPANY,
  SELLAR_CREDIT_GET_STARTED,
} from "../../../constants/routes";
import { NavigationOverlayRow } from "./NavigationOverlayRow";
import { SellarCreditBalance } from "./SellarCreditBalance";

export const SellarCreditNavigationContent = ({
  compact = false,
}: {
  compact?: boolean;
}) => {
  const { business } = useSellarCurrentBusiness();
  const retailerId = business?.Retailer?.id;
  if (!retailerId || business?.type !== "retailer") {
    return null;
  }
  return (
    <SellarCreditForRetailer
      key={business.id}
      retailerId={retailerId}
      compact={compact}
    />
  );
};

const SellarCreditForRetailer = ({
  retailerId,
  compact,
}: {
  retailerId: number;
  compact: boolean;
}) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const query = useLegalEntityCompanyCreditInfo({ RetailerId: retailerId });
  const credit = query.data;
  const isAdmin = user?.type === "admin";
  const isCompanyAdmin = Boolean(
    user?.legalEntityAdminList.some(
      (entry) =>
        entry.legalEntityId === business?.BusinessLegalEntity?.LegalEntityId,
    ),
  );
  const canManage = isAdmin || isCompanyAdmin;
  const canView = Boolean(credit?.userCanViewSellarCreditBalance || canManage);
  const canOpenDashboard =
    canView &&
    Boolean(credit?.userCanAccessSellarCredit || canManage) &&
    Boolean(
      canManage ||
      (business?.BusinessUser?.canAccessSellarCredit &&
        business.BusinessUser.canViewSellarCreditBalance),
    );
  const hasCredit =
    credit?.status === "approved" ||
    (credit?.status === "declined" && (credit.creditAmountInUse ?? 0) > 0);
  let message: string | undefined;
  if (query.isLoading) {
    message = "Loading Sellar Credit…";
  } else if (query.isError) {
    message = "Unable to load Sellar Credit.";
  } else if (!credit) {
    message = "Sellar Credit details are unavailable.";
  } else if (!hasCredit) {
    message =
      credit.status === "eligible" ||
      credit.status === "creditCheckNotPerformed"
        ? "Get started with Sellar Credit to access trade credit."
        : "Sellar Credit is currently unavailable for this company.";
  } else if (!canView) {
    message =
      "Contact your company administrator for permission to view your Sellar Credit balance.";
  }
  const balance = credit && (
    <SellarCreditBalance
      compact={compact}
      available={credit.creditAmountAvailable}
      spend={credit.creditAmountInUse}
      limit={credit.creditLimit}
    />
  );
  const primaryClasses =
    "flex min-h-9 w-full items-center justify-center rounded-lg bg-[#8c23c7] px-3 py-2 text-center text-sm leading-5 font-medium text-white shadow-[0_2px_2px_0_#00000026] hover:bg-[#7b1eaf] focus-visible:outline-2 focus-visible:outline-purple-600";
  return (
    <div
      className={
        compact
          ? "mt-2 shrink-0"
          : "flex min-h-0 flex-col overflow-y-auto overscroll-contain"
      }
    >
      {message ? (
        <div
          role="status"
          className={
            compact
              ? "rounded-2xl border border-zinc-200 p-4 text-sm text-zinc-500"
              : "px-4 py-3 text-sm text-zinc-500"
          }
        >
          {message}
          {query.isError && (
            <button
              type="button"
              className="ml-1 text-purple-700 underline"
              onClick={() => {
                query.refetch();
              }}
            >
              Try again
            </button>
          )}
        </div>
      ) : (
        credit && (
          <>
            {compact && canOpenDashboard ? (
              <Link
                to={`${SELLAR_CREDIT_TAB}?provider=${credit.creditProvider}`}
                aria-label="View Sellar Credit dashboard"
                className="block rounded-2xl focus-visible:outline-2 focus-visible:outline-purple-600"
              >
                {balance}
              </Link>
            ) : (
              balance
            )}
            {credit.status === "declined" && (
              <p className="px-4 py-2 text-sm text-zinc-500">
                New credit is unavailable. Your outstanding spend is shown
                above.
              </p>
            )}
          </>
        )
      )}
      {!compact && !query.isLoading && !query.isError && credit && (
        <div className="flex flex-col gap-2 px-4 pt-3 pb-4">
          {hasCredit && canOpenDashboard && (
            <Link
              className={primaryClasses}
              to={`${SELLAR_CREDIT_TAB}?provider=${credit.creditProvider}`}
            >
              View dashboard
            </Link>
          )}
          {!hasCredit &&
            (credit.status === "eligible" ||
              credit.status === "creditCheckNotPerformed") && (
              <Link className={primaryClasses} to={SELLAR_CREDIT_GET_STARTED}>
                Get started
              </Link>
            )}
          {canManage && (
            <NavigationOverlayRow
              to={SELLAR_CREDIT_MANAGE_COMPANY}
              leading={<Icon icon="COG_OUTLINE" size="16px" />}
              trailing={
                <Icon
                  icon="CHEVRON_RIGHT"
                  size="16px"
                  className="text-zinc-500"
                />
              }
              className="text-zinc-700"
            >
              Manage company
            </NavigationOverlayRow>
          )}
        </div>
      )}
    </div>
  );
};
